import React, { useEffect, useMemo, useState } from "react";
import {
  Sparkles, Loader2, Dumbbell, Salad, MessageCircle, Copy, Check,
  Download, Search, X, Send, Users, ExternalLink,
} from "lucide-react";
import { endpoints } from "../lib/api";
import { openWhatsApp, waLink, markdownToWhatsApp } from "../lib/wa";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";

const KIND_OPTIONS = [
  { key: "both", label: "Workout + Diet" },
  { key: "workout", label: "Workout only" },
  { key: "diet", label: "Diet only" },
];
const GOAL_OPTIONS = [
  { key: "weight_loss", label: "Weight loss" },
  { key: "muscle_gain", label: "Muscle gain" },
  { key: "general_fitness", label: "General fitness" },
  { key: "strength", label: "Strength" },
  { key: "endurance", label: "Endurance" },
  { key: "fight_prep", label: "Fight prep" },
];
const DISCIPLINE_OPTIONS = [
  { key: "strength", label: "Strength training" },
  { key: "hypertrophy", label: "Hypertrophy" },
  { key: "functional", label: "Functional" },
  { key: "calisthenics", label: "Calisthenics" },
  { key: "running", label: "Running" },
  { key: "general", label: "General fitness" },
  { key: "boxing", label: "Boxing" },
  { key: "kickboxing", label: "Kickboxing" },
  { key: "muay_thai", label: "Muay Thai" },
  { key: "mma", label: "MMA" },
  { key: "bjj", label: "BJJ" },
];
const LEVEL_OPTIONS = [
  { key: "beginner", label: "Beginner" },
  { key: "intermediate", label: "Intermediate" },
  { key: "advanced", label: "Advanced" },
];
const DIET_OPTIONS = [
  { key: "veg", label: "Vegetarian" },
  { key: "non_veg", label: "Non-veg" },
  { key: "vegan", label: "Vegan" },
  { key: "jain", label: "Jain" },
  { key: "eggetarian", label: "Eggetarian" },
];
const LANG_OPTIONS = [
  { key: "en", label: "English" },
  { key: "hinglish", label: "Hinglish" },
  { key: "hi", label: "हिंदी" },
];

function FormSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>
          {options.map(o => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

export default function CreatePlanShare() {
  const [members, setMembers] = useState([]);
  const [selected, setSelected] = useState([]); // member ids
  const [query, setQuery] = useState("");
  const [generating, setGenerating] = useState(false);
  const [plan, setPlan] = useState(null);
  const [copied, setCopied] = useState(false);
  const [form, setForm] = useState({
    kind: "both", goal: "general_fitness", level: "beginner",
    days_per_week: 4, dietary_pref: "veg", discipline: "strength",
    lang: "en", notes: "", audience_label: "",
  });

  useEffect(() => {
    endpoints.listMembers().then(r => setMembers(r.data || [])).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return members;
    return members.filter(m =>
      (m.name || "").toLowerCase().includes(q) ||
      (m.phone || "").toLowerCase().includes(q)
    );
  }, [members, query]);

  const toggleMember = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const generate = async (e) => {
    e?.preventDefault();
    if (generating) return;
    setGenerating(true);
    setPlan(null);
    try {
      const { data } = await endpoints.aiPlan({
        kind: form.kind,
        goal: form.goal,
        level: form.level,
        days_per_week: parseInt(form.days_per_week, 10) || 4,
        dietary_pref: form.dietary_pref,
        discipline: form.discipline,
        lang: form.lang,
        notes: form.notes || null,
        audience_label: form.audience_label || null,
      });
      setPlan(data);
      toast.success("Plan generated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate plan");
    } finally { setGenerating(false); }
  };

  const cleaned = useMemo(() => plan ? markdownToWhatsApp(plan.markdown || "") : "", [plan]);

  const copyText = async () => {
    if (!cleaned) return;
    let ok = false;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(cleaned);
        ok = true;
      }
    } catch (_) { /* fall through */ }
    if (!ok) {
      // Fallback: hidden textarea + document.execCommand("copy")
      try {
        const ta = document.createElement("textarea");
        ta.value = cleaned;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        ok = document.execCommand("copy");
        document.body.removeChild(ta);
      } catch (_) { /* fall through */ }
    }
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast.success("Copied to clipboard");
    } else {
      // Last resort: show the text in a prompt for manual copy
      try { window.prompt("Copy this text:", cleaned); } catch (_) {}
      toast.message("Use the dialog to copy manually");
    }
  };

  const downloadTxt = () => {
    if (!cleaned) return;
    try {
      const blob = new Blob([cleaned], { type: "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = (form.audience_label || `${form.discipline}-${form.goal}`).replace(/[^a-z0-9-]+/gi, "-").toLowerCase();
      a.download = `fitnudge-plan-${safe}.txt`;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        try { document.body.removeChild(a); } catch (_) {}
        URL.revokeObjectURL(url);
      }, 1000);
      toast.success("Plan downloaded");
    } catch (e) {
      toast.error("Download blocked - copy the text instead");
    }
  };

  const chosen = useMemo(
    () => members.filter(m => selected.includes(m.id) && m.phone),
    [members, selected]
  );
  const sendIntro = (firstName) =>
    `Hey ${firstName || "there"}, here's your fresh plan from your trainer.`;

  return (
    <section className="border border-border rounded-2xl p-5 md:p-6 bg-card mb-8" data-testid="ai-create-plan-section">
      <div className="flex items-center gap-2.5 mb-1">
        <span
          className="inline-flex items-center justify-center w-9 h-9 rounded-lg text-white shrink-0"
          style={{ background: "linear-gradient(135deg, #7C3AED 0%, #DB2777 60%, #F97316 100%)" }}
          aria-hidden
        >
          <Sparkles className="w-4 h-4" />
        </span>
        <div>
          <h2 className="font-display text-lg font-bold text-foreground leading-none">Create plan & share</h2>
          <p className="text-[11px] text-muted-foreground mt-1">Generate one plan · share with one or many members</p>
        </div>
      </div>

      <form onSubmit={generate} className="mt-5 grid lg:grid-cols-2 gap-5">
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <FormSelect label="Plan type" value={form.kind} onChange={v => setForm({ ...form, kind: v })} options={KIND_OPTIONS} testid="cp-kind" />
            <FormSelect label="Goal" value={form.goal} onChange={v => setForm({ ...form, goal: v })} options={GOAL_OPTIONS} testid="cp-goal" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <FormSelect label="Discipline" value={form.discipline} onChange={v => setForm({ ...form, discipline: v })} options={DISCIPLINE_OPTIONS} testid="cp-discipline" />
            <FormSelect label="Level" value={form.level} onChange={v => setForm({ ...form, level: v })} options={LEVEL_OPTIONS} testid="cp-level" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <FormSelect label="Diet" value={form.dietary_pref} onChange={v => setForm({ ...form, dietary_pref: v })} options={DIET_OPTIONS} testid="cp-diet" />
            <div>
              <Label>Days / week</Label>
              <Input type="number" min={2} max={7} value={form.days_per_week} onChange={e => setForm({ ...form, days_per_week: e.target.value })} data-testid="cp-days" />
            </div>
            <FormSelect label="Language" value={form.lang} onChange={v => setForm({ ...form, lang: v })} options={LANG_OPTIONS} testid="cp-lang" />
          </div>
          <div>
            <Label>Audience label (optional)</Label>
            <Input placeholder='e.g. "Beginner cohort"' value={form.audience_label} onChange={e => setForm({ ...form, audience_label: e.target.value })} data-testid="cp-audience" />
          </div>
          <div>
            <Label>Notes (optional)</Label>
            <Input placeholder="e.g. focus on lower body, no jumps" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} data-testid="cp-notes" />
          </div>
          <button
            type="submit"
            disabled={generating}
            data-testid="cp-generate-btn"
            className="w-full text-white text-sm font-semibold py-2.5 rounded-lg disabled:opacity-60 transition-opacity inline-flex items-center justify-center gap-2"
            style={{ background: "linear-gradient(135deg, #7C3AED 0%, #DB2777 60%, #F97316 100%)" }}
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {generating ? "Generating…" : plan ? "Regenerate plan" : "Generate plan"}
          </button>
        </div>

        {/* Member multi-select */}
        <div>
          <Label className="flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5" /> Share with members
            <span className="ml-auto text-[11px] font-medium text-muted-foreground">{selected.length} selected</span>
          </Label>
          <div className="relative mt-1.5 mb-2">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/70" />
            <Input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search members…"
              className="pl-9"
              data-testid="cp-member-search"
            />
            {query && (
              <button type="button" onClick={() => setQuery("")} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground/70 hover:text-foreground">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <div className="border border-border rounded-lg bg-background/40 max-h-[260px] overflow-y-auto divide-y divide-border/60" data-testid="cp-member-list">
            {filtered.length === 0 && (
              <p className="text-xs text-muted-foreground/70 italic p-4 text-center">No members match.</p>
            )}
            {filtered.map(m => {
              const checked = selected.includes(m.id);
              return (
                <label
                  key={m.id}
                  className={`flex items-center gap-3 p-2.5 cursor-pointer transition-colors ${checked ? "bg-primary/5" : "hover:bg-muted/40"}`}
                  data-testid={`cp-member-row-${m.id}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleMember(m.id)}
                    className="w-4 h-4 accent-primary"
                    data-testid={`cp-member-cb-${m.id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-foreground truncate">{m.name}</div>
                    <div className="text-[11px] text-muted-foreground truncate">{m.phone || "no phone"} · {m.plan}</div>
                  </div>
                </label>
              );
            })}
          </div>
          <div className="flex items-center gap-2 mt-2 text-[11px]">
            <button type="button" onClick={() => setSelected(filtered.map(m => m.id))} className="text-foreground/85 hover:text-foreground" data-testid="cp-select-all">Select all</button>
            <span className="text-muted-foreground/50">·</span>
            <button type="button" onClick={() => setSelected([])} className="text-foreground/85 hover:text-foreground" data-testid="cp-clear">Clear</button>
          </div>
        </div>
      </form>

      {plan && (
        <div className="mt-6 border-t border-border pt-5" data-testid="cp-result">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div>
              <p className="font-display text-sm font-semibold text-foreground">{plan.summary}</p>
              <p className="text-[11px] text-muted-foreground">
                {plan.discipline?.replace(/_/g, " ")} · {plan.goal?.replace(/_/g, " ")} · {plan.level} · {plan.days_per_week}d/wk · {plan.dietary_pref}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button type="button" onClick={copyText} data-testid="cp-copy" className="inline-flex items-center gap-1 text-xs bg-card border border-border hover:bg-muted/50 text-foreground px-2.5 py-1.5 rounded-md transition-colors">
                {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />} {copied ? "Copied" : "Copy"}
              </button>
              <button type="button" onClick={downloadTxt} data-testid="cp-download" className="inline-flex items-center gap-1 text-xs bg-card border border-border hover:bg-muted/50 text-foreground px-2.5 py-1.5 rounded-md transition-colors">
                <Download className="w-3.5 h-3.5" /> Download
              </button>
            </div>
          </div>
          <div className="border border-border rounded-lg p-4 bg-background/40 max-h-[420px] overflow-y-auto" data-testid="cp-markdown">
            <pre className="whitespace-pre-wrap text-sm text-foreground/85 leading-relaxed font-sans">{cleaned}</pre>
          </div>

          {/* Per-member share links - real <a target="_blank"> bypass popup blockers */}
          {chosen.length > 0 && (
            <div className="mt-4" data-testid="cp-send-list">
              <div className="flex items-center gap-2 mb-2">
                <Send className="w-3.5 h-3.5 text-[#25D366]" />
                <span className="text-xs font-semibold text-foreground">Click to send to each member ({chosen.length})</span>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {chosen.map(m => {
                  const first = (m.name || "").split(" ")[0];
                  const text = `${sendIntro(first)}\n\n${cleaned}`;
                  return (
                    <a
                      key={m.id}
                      href={waLink(m.phone, text)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`cp-send-${m.id}`}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-[#25D366] hover:bg-[#1EBE5D] text-white text-xs font-medium transition-colors"
                    >
                      <MessageCircle className="w-3.5 h-3.5 shrink-0" />
                      <span className="flex-1 min-w-0 truncate">{m.name}</span>
                      <ExternalLink className="w-3 h-3 opacity-80 shrink-0" />
                    </a>
                  );
                })}
              </div>
              <p className="text-[11px] text-muted-foreground/70 mt-2">
                Tip - each link opens WhatsApp with the message pre-filled. Click them one by one to reliably hit every chat.
              </p>
            </div>
          )}
          {selected.length > 0 && chosen.length === 0 && (
            <p className="text-[11px] text-rose-500 mt-3">Selected members have no phone numbers on file.</p>
          )}
        </div>
      )}
    </section>
  );
}
